import React from 'react';
import { Box, Typography, Chip, IconButton, LinearProgress } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import GenerationModeBadge from './GenerationModeBadge';
import { calcProgressForWO } from '../hooks/useProgressV2';

const STATUS_COLOR = {
  planned: 'info',
  released: 'info',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'error',
};

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Days overdue for an unfinished WO; 0 / negative means not late.
function daysLate(wo) {
  if (!wo.due_date || wo.status === 'completed' || wo.status === 'cancelled') return 0;
  const due = new Date(String(wo.due_date).slice(0, 10));
  const today = new Date(new Date().toISOString().slice(0, 10));
  return Math.round((today - due) / (1000 * 60 * 60 * 24));
}

// The step the WO is currently on: first not-completed step, else the last.
function currentStepOf(wo) {
  if (!Array.isArray(wo.steps) || wo.steps.length === 0) return null;
  const sorted = [...wo.steps].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  return sorted.find((s) => s.status !== 'completed') || sorted[sorted.length - 1];
}

export default function WOv2Card({ wo, onOpen, compact = false, actions = null }) {
  if (!wo) return null;
  const progress = calcProgressForWO(wo);
  const showProgress = progress.produced > 0 || wo.status === 'in_progress';
  const late = daysLate(wo);
  const step = currentStepOf(wo);
  return (
    <Box
      data-testid={`wo-v2-card-${wo.id}`}
      onClick={onOpen ? () => onOpen(wo) : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(wo); }
      } : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: compact ? 1 : 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        cursor: onOpen ? 'pointer' : 'default',
        background: 'background.paper',
        '&:hover': onOpen ? { background: 'action.hover' } : undefined,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Item name + code lead - that's what a user scans for; the WO
            number is bookkeeping and sits on the second line. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {wo.output_item_code} — {wo.output_item_name}
          </Typography>
          <GenerationModeBadge />
          <Chip
            size="small"
            label={wo.status}
            color={STATUS_COLOR[wo.status] || 'default'}
          />
          {wo.is_top_level && (
            <Chip size="small" label="Top-level" variant="outlined" />
          )}
        </Box>
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {wo.wo_number}
          {wo.customer_order_number ? ` · Order ${wo.customer_order_number}` : ''}
          {wo.line_number != null ? ` (line ${wo.line_number})` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Qty {wo.quantity_to_produce}
          {showProgress ? ` (${progress.produced} done)` : ''}
          {' · '}
          <Box component="span" sx={late > 0 ? { color: 'error.main', fontWeight: 700 } : undefined}>
            Due {formatDate(wo.due_date)}{late > 0 ? ` (${late}d late)` : ''}
          </Box>
          {step ? ` · ${step.operation_code}${step.operation_name ? ` ${step.operation_name}` : ''}` : ''}
          {step && step.total_steps > 1 ? ` (step ${step.sequence}/${step.total_steps})` : ''}
        </Typography>
        {showProgress && (
          <LinearProgress
            variant="determinate"
            value={progress.percent_complete}
            color={progress.percent_complete >= 100 ? 'success' : 'primary'}
            sx={{ mt: 0.5, height: 4, borderRadius: 2, maxWidth: 320 }}
            data-testid={`wo-progress-${wo.id}`}
          />
        )}
      </Box>
      {actions && (
        // Stop clicks on action buttons from also triggering the card's onOpen.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          {actions}
        </Box>
      )}
      {onOpen && (
        <IconButton size="small" aria-label="open detail">
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
}
